import { NgModule, Type, inject } from '@angular/core';
import { ActivatedRouteSnapshot, RouterModule, RouterStateSnapshot, Routes } from '@angular/router';
import * as pages from '../../core/routing/page-names';
import { ImportConfigurationComponent } from './import-configuration/import-configuration.component';
import { ImportConfigurationProtocolComponent } from './import-configuration-protocol/import-configuration-protocol.component';
import { FieldAssignmentDefinitionsService } from './shared/field-assignment-definitions.service';
import { CanDeactivateGuard } from 'src/app/base/guards/can-deactivate-guard.service';
import { RoleService } from 'src/app/base/services/role.service';
import { ConfigurationsComponent } from '../export-configuration/configurations/configurations.component';
import { AcodisWorkflowComponent } from './acodis-workflow/acodis-workflow.component';
import { ConfigurationInterfaceGuard } from 'src/app/base/guards/configuration-interface.guard';
import { ImportConfigurationEditFormsComponent } from './shared/import-configuration-edit-forms/import-configuration-edit-forms.component';
import { ImportConfigurationTemplateComponent } from './import-configuration-template/import-configuration-template.component';
import { AdminGuard } from 'src/app/base/guards/admin.guard';
import { ImportConfigurationGlobalComponent } from './import-configuration-global/import-configuration-global.component';
import { ImportConfigurationClientAccessType } from 'src/app/base/models/models.swagger.generated';

function createEditRouteChildren(component: Type<any>, supportResponseConfig: boolean): Routes {
  let routes: Routes = [
    {
      path: '',
      pathMatch: 'full',
      redirectTo: pages.configurationGeneralPageName
    },
    {
      path: pages.configurationGeneralPageName,
      component: component,
      resolve: {
        roleInfo:()=>{return inject(RoleService).getRoleMappingInfo();}
      }
    },
    {
      path: pages.configurationTimeSchedulePageName,
      component: component,
    },
    {
      path: pages.configurationEmailPageName,
      component: component,
    },
    {
      path: pages.configurationSftpPageName,
      component: component,
    },
    {
      path: pages.configurationFtpPageName,
      component: component,
    },
    {
      path: pages.configurationRestPageName,
      component: component,
    },
    {
      path: pages.configurationAs2PageName,
      component: component,
    },
    {
      path: pages.configurationAcodisPageName,
      component: component,
    },
    {
      path: pages.configurationManualImportPageName,
      component: component,
    },
    {
      path: pages.configurationFilePageName,
      component: component,
      children: [
        {
          path: '',
          pathMatch: 'full',
          redirectTo: pages.configurationFileGeneralPageName
        },
        {
          path: pages.configurationFileGeneralPageName,
          component: component,
        },
        {
          path: pages.configurationFileColumnBasedPageName,
          component: component,
        },
        {
          path: pages.configurationFileEdifactPageName,
          component: component,
        },
        {
          path: pages.configurationFileAcodisPageName,
          component: component,
        },
        {
          path: pages.configurationFileFilterPageName,
          component: component,
        },
      ]
    }
  ];
  if (supportResponseConfig) {
    routes.push(
      {
        path: pages.configurationResponsePageName,
        component: component,
        children: [
          {
            path: '',
            pathMatch: 'full',
            component: ConfigurationsComponent
          },
          {
            path: ":id",
            component: ConfigurationsComponent,
            canDeactivate: [(component: ConfigurationsComponent) => inject(CanDeactivateGuard).canDeactivate(component)],
            children: [
              {
                path: '',
                pathMatch: 'full',
                redirectTo: pages.configurationGeneralPageName
              },
              {
                path: pages.configurationGeneralPageName,
                component: ConfigurationsComponent,
                resolve: {
                  roleInfo:()=>{return inject(RoleService).getRoleMappingInfo();}
                }
              },
              {
                path: pages.configurationBacklogPageName,
                component: ConfigurationsComponent,
              },
              {
                path: pages.configurationFinancePageName,
                component: ConfigurationsComponent,
              },
              {
                path: pages.configurationTimeSchedulePageName,
                component: ConfigurationsComponent,
              },
              {
                path: pages.configurationEmailPageName,
                component: ConfigurationsComponent,
              },
              {
                path: pages.configurationSftpPageName,
                component: ConfigurationsComponent,
              },
              {
                path: pages.configurationFtpPageName,
                component: ConfigurationsComponent,
              },
              {
                path: pages.configurationRestPageName,
                component: ConfigurationsComponent,
              },
              {
                path: pages.configurationFilePageName,
                component: ConfigurationsComponent,
                children: [
                  {
                    path: '',
                    pathMatch: 'full',
                    redirectTo: pages.configurationFileGeneralPageName
                  },
                  {
                    path: pages.configurationFileGeneralPageName,
                    component: ConfigurationsComponent,
                  },
                  {
                    path: pages.configurationFileColumnBasedPageName,
                    component: ConfigurationsComponent,
                  },
                  {
                    path: pages.configurationFileLineBasedPageName,
                    component: ConfigurationsComponent,
                  },
                  {
                    path: pages.configurationFileFilterPageName,
                    component: ConfigurationsComponent,
                  },
                ]
              },
            ]
          },
          {
            path: pages.exportConfigurationCreatePageName,
            component: ConfigurationsComponent,
          },
        ]
      },
    );
  }
  return routes;
}

const routes: Routes = [
  {
    path: pages.importConfigurationsPageName,
    canActivate: [ConfigurationInterfaceGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: pages.importConfigurationsConfigurationPageName
      },
      {
        path: pages.importConfigurationsConfigurationPageName,
        component: ImportConfigurationComponent,
        resolve: {
          fieldAssignmentDefinitions: () => inject(FieldAssignmentDefinitionsService).loadAllFiledAssignmentDefinitions(ImportConfigurationClientAccessType.Client),
        },
        children: [
          {
            path: '',
            pathMatch: 'full',
            component: ImportConfigurationComponent
          },
          {
            path: ":id",
            component: ImportConfigurationComponent,
            canDeactivate: [(component: ImportConfigurationEditFormsComponent) => inject(CanDeactivateGuard).canDeactivate(component)],
            children: createEditRouteChildren(ImportConfigurationComponent, true)
          },
          {
            path: pages.importConfigurationCreatePageName,
            component: ImportConfigurationComponent,
            canDeactivate: [(component: ImportConfigurationEditFormsComponent) => inject(CanDeactivateGuard).canDeactivate(component)],
          },
        ]
      },
      {
        path: pages.configurationTemplatesPageName,
        component: ImportConfigurationTemplateComponent,
        resolve: {
          fieldAssignmentDefinitions: () => inject(FieldAssignmentDefinitionsService).loadAllFiledAssignmentDefinitions(undefined),
        },
        children: [
          {
            path: '',
            pathMatch: 'full',
            component: ImportConfigurationTemplateComponent
          },
          {
            path: ":id",
            component: ImportConfigurationTemplateComponent,
            canDeactivate: [(component: ImportConfigurationEditFormsComponent) => inject(CanDeactivateGuard).canDeactivate(component)],
            children: createEditRouteChildren(ImportConfigurationTemplateComponent, false)
          },
          {
            path: pages.importConfigurationCreatePageName,
            component: ImportConfigurationTemplateComponent,
            canDeactivate: [(component: ImportConfigurationEditFormsComponent) => inject(CanDeactivateGuard).canDeactivate(component)],
          },
        ]
      },
      {
        path: pages.importConfigurationsGlobalPageName,
        component: ImportConfigurationGlobalComponent,
        canActivate: [AdminGuard],
        resolve: {
          fieldAssignmentDefinitions: () => inject(FieldAssignmentDefinitionsService).loadAllFiledAssignmentDefinitions(ImportConfigurationClientAccessType.Global),
        },
        children: [
          {
            path: '',
            pathMatch: 'full',
            component: ImportConfigurationTemplateComponent
          },
          {
            path: ":id",
            component: ImportConfigurationTemplateComponent,
            canDeactivate: [(component: ImportConfigurationEditFormsComponent) => inject(CanDeactivateGuard).canDeactivate(component)],
            children: createEditRouteChildren(ImportConfigurationGlobalComponent, false)
          },
          {
            path: pages.importConfigurationCreatePageName,
            component: ImportConfigurationGlobalComponent,
            canDeactivate: [(component: ImportConfigurationEditFormsComponent) => inject(CanDeactivateGuard).canDeactivate(component)],
          },
        ]
      },
      {
        path: pages.importConfigurationsProtocolPageName,
        component: ImportConfigurationProtocolComponent,
        children: [
          {
            path: '',
            pathMatch: 'full',
            component: ImportConfigurationProtocolComponent
          },
          {
            path: ":id",
            component: ImportConfigurationProtocolComponent
          }
        ]
      },
      {
        path: pages.acodisWorkflowsPageName,
        component: AcodisWorkflowComponent,
        canActivate: [AdminGuard],
        children: [
          {
            path: '',
            pathMatch: 'full',
            component: AcodisWorkflowComponent
          },
          {
            path: ":id",
            component: AcodisWorkflowComponent
          }
        ]
      },
    ]
  }
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class ImportConfigurationRoutingModule { }
